import React, { createContext, useState, useEffect, ReactNode } from "react";
import api from "../api/axios";

export interface AuthUser {
  id: number | null;
  email: string;
  role: string;
  tenantId: number | null;
  tenantName?: string;
  name?: string;
  token: string;
  impersonationMode?: boolean;
  effectiveRole?: string;
  impersonatedAt?: string;
}

export interface UserPermission {
  module: string;
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
}

export interface AuthContextType {
  user: AuthUser | null;
  platformUser: AuthUser | null; // 🛡️ Dedicated platform session
  permissions: UserPermission[];
  isAuthenticated: boolean;
  isPlatformAuthenticated: boolean;
  login: (data: AuthUser) => void;
  platformLogin: (data: AuthUser) => void;
  logout: () => void;
  platformLogout: () => void;
  refreshPermissions: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  platformUser: null,
  permissions: [],
  isAuthenticated: false,
  isPlatformAuthenticated: false,
  login: () => {},
  platformLogin: () => {},
  logout: () => {},
  platformLogout: () => {},
  refreshPermissions: async () => {},
});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [platformUser, setPlatformUser] = useState<AuthUser | null>(null);
  const [permissions, setPermissions] = useState<UserPermission[]>([]);

  const refreshPermissions = async () => {
    const savedUser = localStorage.getItem("user");
    let token = user?.token;
    if (!token && savedUser && savedUser !== "undefined") {
      try {
        token = JSON.parse(savedUser).token;
      } catch {}
    }
    if (!token) {
      setPermissions([]);
      return;
    }
    try {
      const res = await api.get("/tenant-users/my-permissions");
      setPermissions(res.data.permissions || []);
    } catch (err) {
      console.error("Failed to fetch user permissions:", err);
    }
  };

  useEffect(() => {
    if (user) {
      refreshPermissions();
    } else {
      setPermissions([]);
    }
  }, [user?.token, user?.effectiveRole]);

  // Load saved sessions on mount
  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    const savedPlatform = localStorage.getItem("platform_user");

    try {
      if (savedUser && savedUser !== "undefined") setUser(JSON.parse(savedUser));
      if (savedPlatform && savedPlatform !== "undefined") setPlatformUser(JSON.parse(savedPlatform));
    } catch {
      localStorage.removeItem("user");
      localStorage.removeItem("platform_user");
    }
  }, []);

  // 🔄 CROSS-TAB SYNC: localStorage is shared per-origin, so a logout (idle
  // timeout or manual) in one tab silently wipes the session for every other
  // open tab. Without this, those other tabs keep their stale in-memory
  // `user`/`platformUser` state, keep attaching a token that no longer
  // exists in storage on the NEXT request, and get stuck — the request goes
  // out with no Authorization header, the API returns "No token provided",
  // and the page just fails to load data with no indication why. This
  // listener mirrors storage changes into React state immediately, on every
  // other tab, so they redirect to login the moment the session actually
  // disappears instead of on the next failed request.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "user") {
        if (!e.newValue) {
          setUser(null);
          if (!window.location.pathname.startsWith("/platform") && !window.location.pathname.startsWith("/login")) {
            window.location.href = "/login?expired=true";
          }
        } else if (e.newValue !== "undefined") {
          try { setUser(JSON.parse(e.newValue)); } catch {}
        }
      }

      if (e.key === "platform_user") {
        if (!e.newValue) {
          setPlatformUser(null);
          if (window.location.pathname.startsWith("/platform") && !window.location.pathname.startsWith("/platform/login")) {
            window.location.href = "/platform/login?expired=true";
          }
        } else if (e.newValue !== "undefined") {
          try { setPlatformUser(JSON.parse(e.newValue)); } catch {}
        }
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  /**
   * 🏘️ TENANT LOGIN: Standard access to society ERP
   */
  const login = (data: AuthUser) => {
    setUser(data);
    localStorage.setItem("user", JSON.stringify(data));
  };

  /**
   * 🏛️ PLATFORM LOGIN: Infrastructure-level control session
   */
  const platformLogin = (data: AuthUser) => {
    setPlatformUser(data);
    localStorage.setItem("platform_user", JSON.stringify(data));
  };

  /**
   * 🏘️ TENANT LOGOUT
   */
  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
  };

  /**
   * 🏛️ PLATFORM LOGOUT
   */
  const platformLogout = () => {
    setPlatformUser(null);
    localStorage.removeItem("platform_user");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        platformUser,
        permissions,
        isAuthenticated: !!user,
        isPlatformAuthenticated: !!platformUser,
        login,
        platformLogin,
        logout,
        platformLogout,
        refreshPermissions,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
